import { getRulesStore } from '../storage/stores';
import {
  getDateInFuture,
  getDateMs,
  getHour,
  getSystemTimezone,
  getWeekdayValue,
} from '../lib/dateTime';
import { TimeRestrictionsStore } from '../types/store';
import { shouldCheckNavigation } from '../helper/rules';

type ScheduleContext = {
  timeRestrictions: TimeRestrictionsStore;
  timezone: string;
};

const getScheduleContext = (): ScheduleContext | undefined => {
  const rulesStore = getRulesStore();
  const timeRestrictions = rulesStore.getTimeRestrictions();
  const timezone = rulesStore.getTimezone();

  if (!timeRestrictions || !timezone) {
    return undefined;
  }

  return { timeRestrictions, timezone };
};

const isHourRestrictedAt = (
  { timeRestrictions, timezone }: ScheduleContext,
  date: number,
  { shouldLog = false }: { shouldLog?: boolean } = {}
): boolean => {
  const dailySchedule = getWeekdayValue(
    timeRestrictions.time_ranges,
    date,
    timezone
  );
  return isHourRestricted(dailySchedule, getHour(date, timezone), {
    shouldLog,
  });
};

export const isScheduleRestricted = (
  date: number,
  { shouldLog = false }: { shouldLog?: boolean } = {}
) => {
  const ctx = getScheduleContext();
  if (!ctx) {
    if (shouldLog) {
      console.log(
        '[isScheduleRestricted] false - schedule context missing (no timeRestrictions or timezone in rules store)'
      );
    }
    return false;
  }

  if (!isScheduleRestrictedByRules(ctx.timeRestrictions, { shouldLog })) {
    if (shouldLog) {
      console.log(
        '[isScheduleRestricted] false - schedule restrictions disabled by rules'
      );
    }
    return false;
  }

  const isRestricted = isHourRestrictedAt(ctx, date, { shouldLog });

  if (shouldLog) {
    const systemTimezone = getSystemTimezone();
    console.log(
      `[isScheduleRestricted] ${isRestricted} - date=${date}, timezone=${ctx.timezone}, systemTimezone=${systemTimezone}`
    );
  }

  return isRestricted;
};

const isScheduleRestrictedByRules = (
  timeRestrictions: TimeRestrictionsStore,
  { shouldLog = false }: { shouldLog?: boolean } = {}
): boolean => {
  const isEnabled = isScheduleRestrictionsEnabled(timeRestrictions, {
    shouldLog,
  });
  const shouldCheck = shouldCheckNavigation(timeRestrictions);
  const result = isEnabled && shouldCheck;

  if (shouldLog) {
    console.log(
      `[isScheduleRestrictedByRules] ${result} - isScheduleRestrictionsEnabled=${isEnabled}, shouldCheckNavigation=${shouldCheck}`
    );
  }

  return result;
};

const isScheduleRestrictionsEnabled = (
  timeRestrictions: TimeRestrictionsStore,
  { shouldLog = false }: { shouldLog?: boolean } = {}
): boolean => {
  const result = timeRestrictions.is_daily_limit;

  if (shouldLog) {
    console.log(
      `[isScheduleRestrictionsEnabled] ${result} - is_daily_limit=${timeRestrictions.is_daily_limit}`
    );
  }

  return result;
};

const isHourRestricted = (
  dayHoursRules: number,
  hour: number,
  { shouldLog = false }: { shouldLog?: boolean } = {}
): boolean => {
  const hourMask = 1 << hour;
  const result = (dayHoursRules & hourMask) !== 0;

  if (shouldLog) {
    console.log(
      `[isHourRestricted] ${result} - dayHoursRules=${dayHoursRules}, hour=${hour}, hourMask=${hourMask}`
    );
  }

  return result;
};

const HOURS_IN_WEEK = 7 * 24;

/**
 * Finds the next allowed hour required by the remote schedule blocker.
 */
export const getNextAvailableHour = (date: number): number | undefined => {
  const ctx = getScheduleContext();
  if (!ctx) {
    return undefined;
  }

  for (let offset = 1; offset <= HOURS_IN_WEEK; offset++) {
    const futureMs = getDateMs(
      getDateInFuture({ hours: offset }, date, ctx.timezone)
    );
    if (!isHourRestrictedAt(ctx, futureMs)) {
      return getHour(futureMs, ctx.timezone);
    }
  }

  return undefined;
};
